import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, eachDayOfInterval, isSameDay,
  isSameMonth, isToday, setYear, getYear, parseISO,
} from 'date-fns';
import { de, cs, es, enUS } from 'date-fns/locale';
import { useTranslation } from '../i18n/LanguageContext';
import { useThemeColor } from '../hooks/useThemeColor';
import { fetchLoginBranding } from '../slice/themeSlice';
import { BASE_AUTH_URL } from '../components/constants';

const DATE_LOCALES = { de, cs, es, en: enUS };
const PHONE_REGEX = /^[+]?[0-9\s\-()]{7,20}$/;

const EyeIcon = ({ visible }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.6 18.6 0 0 1 4.22-5.06M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" />
    )}
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const Spinner = ({ color }) => (
  <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: color || '#6B7280' }}>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside]);
};

const FloatingField = ({ label, trailing, inputRef, ...props }) => (
  <div className="relative">
    <label className="absolute -top-2 left-3.5 px-1.5 bg-white text-[11px] text-gray-500 z-10">
      {label}
    </label>
    <input
      ref={inputRef}
      {...props}
      className="w-full border border-gray-300 rounded-2xl px-4 py-[14px] text-[15px] text-gray-900 outline-none focus:border-2 focus:border-[var(--aispace-color)] transition-[border] disabled:bg-gray-50"
    />
    {trailing && (
      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">{trailing}</div>
    )}
  </div>
);

const DatePicker = ({ label, value, onChange, primaryColor, t, language }) => {
  const dateLocale = DATE_LOCALES[language] || enUS;
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('days');
  const [viewDate, setViewDate] = useState(value ? parseISO(value) : new Date());
  const containerRef = useRef(null);
  useClickOutside(containerRef, () => { setOpen(false); setViewMode('days'); });

  const selectedDate = value ? parseISO(value) : null;
  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const days = eachDayOfInterval({ start: startOfWeek(monthStart), end: endOfWeek(monthEnd) });
  const weekDayLabels = eachDayOfInterval({ start: startOfWeek(new Date()), end: endOfWeek(new Date()) })
    .map((d) => format(d, 'EEEEE', { locale: dateLocale }));
  const yearRangeStart = Math.floor(getYear(viewDate) / 12) * 12;
  const years = Array.from({ length: 12 }, (_, i) => yearRangeStart + i);

  const selectDay = (day) => {
    onChange(format(day, 'yyyy-MM-dd'));
    setOpen(false);
    setViewMode('days');
  };

  return (
    <div className="relative" ref={containerRef}>
      <label className="absolute -top-2 left-3.5 px-1.5 bg-white text-[11px] text-gray-500 z-10">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full border border-gray-300 rounded-2xl px-4 py-[14px] text-[15px] text-left outline-none focus:border-2 focus:border-[var(--aispace-color)] transition-[border] flex items-center justify-between"
      >
        <span className={selectedDate ? 'text-gray-900' : 'text-gray-400'}>
          {selectedDate ? format(selectedDate, 'dd.MM.yyyy') : 'TT.MM.JJJJ'}
        </span>
        <span className="text-gray-400"><CalendarIcon /></span>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl p-4 w-[300px]">
          {viewMode === 'days' ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => setViewDate((d) => subMonths(d, 1))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100">
                  <ChevronLeftIcon />
                </button>
                <button type="button" onClick={() => setViewMode('years')} className="text-[14px] font-semibold text-gray-900 hover:opacity-70">
                  {format(viewDate, 'MMMM yyyy', { locale: dateLocale })}
                </button>
                <button type="button" onClick={() => setViewDate((d) => addMonths(d, 1))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100">
                  <ChevronRightIcon />
                </button>
              </div>
              <div className="grid grid-cols-7 mb-1">
                {weekDayLabels.map((d, i) => (
                  <div key={i} className="text-center text-[11px] text-gray-400 font-medium py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-1">
                {days.map((day) => {
                  const inMonth = isSameMonth(day, viewDate);
                  const sel = selectedDate && isSameDay(day, selectedDate);
                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => selectDay(day)}
                      className={`h-9 w-9 rounded-full text-[13px] flex items-center justify-center mx-auto transition
                        ${!inMonth ? 'text-gray-300' : 'text-gray-700'}
                        ${sel ? 'text-white font-medium' : 'hover:bg-gray-100'}
                        ${!sel && isToday(day) ? 'ring-1 ring-gray-300' : ''}`}
                      style={sel ? { backgroundColor: primaryColor } : undefined}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <button type="button" onClick={() => { onChange(''); setOpen(false); }} className="text-[13px] font-medium" style={{ color: primaryColor }}>
                  {t('auth.clear')}
                </button>
                <button type="button" onClick={() => selectDay(new Date())} className="text-[13px] font-medium" style={{ color: primaryColor }}>
                  {t('auth.today')}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => setViewDate((d) => setYear(d, getYear(d) - 12))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100">
                  <ChevronLeftIcon />
                </button>
                <span className="text-[14px] font-semibold text-gray-900">{years[0]} – {years[11]}</span>
                <button type="button" onClick={() => setViewDate((d) => setYear(d, getYear(d) + 12))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100">
                  <ChevronRightIcon />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => { setViewDate((d) => setYear(d, year)); setViewMode('days'); }}
                    className={`py-2 rounded-lg text-[13px] hover:bg-gray-100 ${year === getYear(viewDate) ? 'font-semibold' : 'text-gray-700'}`}
                    style={year === getYear(viewDate) ? { color: primaryColor } : undefined}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const InviteRegister = () => {
  const { t, language } = useTranslation();
  const { primaryColor } = useThemeColor();
  const dispatch = useDispatch();

  const [pageState, setPageState] = useState('loading'); // 'loading' | 'form' | 'error'
  const [email, setEmail] = useState('');
  const [signupToken, setSignupToken] = useState('');
  const [fullname, setFullname] = useState('');
  const [dob, setDob] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setError(t('auth.genericError'));
      setPageState('error');
      return;
    }
    axios.post(`${BASE_AUTH_URL}/verify-invite`, { invite_token: token })
      .then((res) => {
        if (res.data.needs_profile) {
          const invitedEmail = res.data.email;
          setEmail(invitedEmail);
          setSignupToken(res.data.signup_token);
          dispatch(fetchLoginBranding(invitedEmail));
          setPageState('form');
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || t('auth.genericError'));
        setPageState('error');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullname.trim() || !dob || !mobileNumber.trim()) {
      setError(t('auth.fieldRequired'));
      return;
    }
    if (!PHONE_REGEX.test(mobileNumber.trim())) {
      setError(t('auth.invalidPhone'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_AUTH_URL}/complete-signup`,
        { email, fullname, date_of_birth: dob, mobile_number: mobileNumber, password },
        { headers: { Authorization: `Bearer ${signupToken}` } },
      );
      const data = res.data;
      if (data.needs_mfa_setup) {
        window.location.replace(
          `/login?email=${encodeURIComponent(data.email)}&mfa_setup_token=${encodeURIComponent(data.mfa_setup_token)}`,
        );
        return;
      }
      if (data.needs_mfa) {
        window.location.replace(
          `/login?email=${encodeURIComponent(data.email)}&mfa_token=${encodeURIComponent(data.mfa_token)}`,
        );
        return;
      }
      localStorage.setItem('token', data.token);
      localStorage.setItem('email', data.email);
      localStorage.setItem('name', data.name);
      localStorage.setItem('id', data.id);
      localStorage.setItem('language', 'de');
      window.location.replace('/');
    } catch (err) {
      setError(err?.response?.data?.detail || t('auth.genericError'));
    } finally {
      setLoading(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F2]">
        <Spinner color={primaryColor} />
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F2] p-6">
        <div className="bg-white rounded-[28px] shadow-[0_2px_40px_rgba(0,0,0,0.08)] w-full max-w-[440px] px-10 py-12 text-center">
          <p className="text-[15px] text-red-600">{error || t('auth.genericError')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F2F2F2] p-6">
      <div className="bg-white rounded-[28px] shadow-[0_2px_40px_rgba(0,0,0,0.08)] w-full max-w-[440px] px-10 py-12">
        <form onSubmit={handleSubmit}>
          <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">
            {t('auth.completeProfileTitle')}
          </h1>
          <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
            {t('auth.completeProfileSubtitle')}
          </p>

          {error && (
            <div className="mb-5 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <FloatingField
              label={t('auth.fullnameLabel')}
              type="text"
              value={fullname}
              onChange={(e) => setFullname(e.target.value)}
              autoFocus
            />

            <DatePicker
              label={t('auth.dobLabel')}
              value={dob}
              onChange={setDob}
              primaryColor={primaryColor}
              t={t}
              language={language}
            />

            <FloatingField
              label={t('auth.mobileLabel')}
              type="tel"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
            />

            <FloatingField
              label={t('auth.passwordLabel')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              trailing={(
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="pointer-events-auto">
                  <EyeIcon visible={showPassword} />
                </button>
              )}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-7 py-[14px] rounded-full text-white text-[15px] font-medium transition hover:brightness-90 active:brightness-75 disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: primaryColor }}
          >
            {loading ? (
              <Spinner color="white" />
            ) : (
              t('common.continue')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InviteRegister;
